import { EmptyError, firstValueFrom, Subscription, type Observable } from 'rxjs';

/**
 * True when an error is the rejection produced by an aborted `AbortSignal`.
 * Use in catch blocks around abortable API calls to skip logging cancellations.
 */
export function isAbortError(error: unknown): boolean {
  // Structural check instead of `instanceof DOMException`: abort reasons can
  // originate from another realm (e.g. Node's AbortController under jsdom).
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

function abortReason(signal: AbortSignal): DOMException {
  const reason = signal.reason;
  if (isAbortError(reason)) {
    return reason as DOMException;
  }
  if (reason instanceof Error) {
    return new DOMException(reason.message || 'Aborted', 'AbortError');
  }
  return new DOMException(reason != null ? String(reason) : 'Aborted', 'AbortError');
}

/**
 * Like `firstValueFrom`, but tears the subscription down when `signal` aborts.
 *
 * Angular's `HttpClient` ignores a `signal` request option — cancellation only
 * happens via unsubscribe. This bridges an `AbortSignal` (e.g. `resource()`'s
 * `abortSignal`) to that unsubscribe so aborted requests are actually
 * cancelled on the network instead of running to completion.
 */
export function firstValueFromAbortable<T>(
  source$: Observable<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (!signal) {
    return firstValueFrom(source$);
  }
  if (signal.aborted) {
    return Promise.reject(abortReason(signal));
  }

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    // Eager container so the abort/next handlers can tear down even when the
    // source emits synchronously (adding to a closed Subscription disposes
    // the inner subscription immediately).
    const sub = new Subscription();

    const onAbort = () => {
      if (settled) {
        return;
      }
      settled = true;
      sub.unsubscribe();
      reject(abortReason(signal));
    };

    const settle = (complete: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      signal.removeEventListener('abort', onAbort);
      complete();
    };

    signal.addEventListener('abort', onAbort, { once: true });

    sub.add(
      source$.subscribe({
        next: (value) => {
          settle(() => resolve(value));
          sub.unsubscribe();
        },
        error: (error) => settle(() => reject(error)),
        complete: () => settle(() => reject(new EmptyError())),
      }),
    );
  });
}
